import { ReactNode } from 'react';
import clsx from 'clsx';
import { useStyles } from './custom-select-box.style';
import { generateUniqueId } from '../../shared/helpers/helpers';

export enum SELECT_BEHAVIOUR {
    RADIO = 'radio',
    CHECKBOX = 'checkbox',
}

type CsbOptionProps = {
    /**
     * The html label for the option
     */
    label: string | ReactNode;
    /**
     * The value for the option
     */
    value: string;
    /**
     * Name of flos-icon.
     */
    icon?: string;
    /**
     * The size of the icon in pixels, default 60px
     */
    iconSize?: string;
    /**
     * The location of the icon, default above
     */
    iconLocation?: 'left' | 'right' | 'above' | 'below';
    /**
     * If this radio button will be selected
     */
    checked?: boolean;
    /**
     * If this radio button will be disabled
     */
    disabled?: boolean;
    /**
     * Set slim margin for icon, left or right
     */
    slimSideMargin?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

// these props are provided by the parent component
type CsbOptionItemProps = {
    index?: number;
    name?: string;
    showSelected?: boolean;
    isSubmitted?: boolean;
    onChangeHandler?: (value: string, index: number) => void;
    behaviour?: string;
} & CsbOptionProps;

const CsbOption = ({
    label,
    value,
    icon,
    iconSize,
    iconLocation,
    checked,
    disabled,
    index,
    name,
    showSelected,
    isSubmitted,
    slimSideMargin,
    onChangeHandler = () => ({}),
    behaviour,
    ...rest
}: CsbOptionItemProps) => {
    const classes = useStyles();
    const radioId = `radio-${generateUniqueId()}`;

    return (
        <>
            <label
                className={clsx(
                    classes.radioButtonLabel,
                    checked && !disabled
                        ? classes.radioButtonLabelSelected
                        : isSubmitted && !checked
                        ? classes.radioButtonLabelError
                        : '',
                    disabled ? classes.radioDisabled : '',
                    icon && (iconLocation === 'left' || iconLocation === 'right') ? classes.iconLeftRight : ''
                )}
                htmlFor={radioId}
            >
                <input
                    type={behaviour === SELECT_BEHAVIOUR.RADIO ? 'radio' : 'checkbox'}
                    name={name}
                    value={value}
                    id={radioId}
                    disabled={disabled}
                    checked={checked}
                    onChange={() => {
                        if (!disabled) {
                            onChangeHandler(value, index || 0);
                        }
                    }}
                    onClick={() => {
                        if (!disabled && behaviour === SELECT_BEHAVIOUR.RADIO && checked) {
                            onChangeHandler(value, index || 0);
                        }
                    }}
                    {...rest}
                />
                <IconLabel
                    label={label}
                    icon={icon}
                    iconSize={iconSize}
                    iconLocation={iconLocation}
                    slimSideMargin={slimSideMargin}
                />
                {showSelected && checked && (
                    <span className={`${classes.radioButtonLabelSelect}`}>
                        <span>VALGT</span>
                    </span>
                )}
            </label>
        </>
    );
};

interface IIconLabel {
    label: string | ReactNode;
    icon?: string;
    iconSize?: string;
    iconLocation?: string;
    slimSideMargin?: boolean;
}
const IconLabel = ({
    label,
    icon,
    iconSize = '60',
    iconLocation = 'above',
    slimSideMargin,
}: IIconLabel): JSX.Element => {
    const classes = useStyles();
    switch (iconLocation) {
        case 'left':
            return (
                <>
                    {icon && <flos-icon shape={icon} size={iconSize} />}
                    <span
                        className={clsx(
                            classes.radioLabel,
                            !icon
                                ? classes.nonIconLabel
                                : slimSideMargin
                                ? classes.slimSideMarginLeft
                                : classes.iconLeft
                        )}
                        color="textPrimary"
                    >
                        {label}
                    </span>
                </>
            );
        case 'right':
            return (
                <>
                    <span
                        className={clsx(
                            classes.radioLabel,
                            !icon
                                ? classes.nonIconLabel
                                : slimSideMargin
                                ? classes.slimSideMarginRight
                                : classes.iconRight
                        )}
                        color="textPrimary"
                    >
                        {label}
                    </span>
                    {icon && <flos-icon shape={icon} size={iconSize} />}
                </>
            );
        case 'below':
            return (
                <>
                    <span className={clsx(classes.radioLabel, !icon ? classes.nonIconLabel : '')} color="textPrimary">
                        {label}
                    </span>
                    {icon && <flos-icon shape={icon} size={iconSize} />}
                </>
            );
        default:
            return (
                <>
                    {icon && <flos-icon shape={icon} size={iconSize} />}
                    <span className={clsx(classes.radioLabel, !icon ? classes.nonIconLabel : '')} color="textPrimary">
                        {label}
                    </span>
                </>
            );
    }
};

export type { CsbOptionProps };
export type { CsbOptionItemProps };
export { CsbOption };
